import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile, BottomSheet } from './LanguageSwitcher';

function Initials({ name }) {
    const text = (name || '?')
        .split(/\s+/)
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase() || '?';
    return (
        <span
            className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full font-display text-xs font-bold text-white shadow-neon"
            style={{
                background:
                    'linear-gradient(135deg, rgb(var(--c-secondary)) 0%, rgb(var(--c-accent)) 100%)',
            }}
            aria-hidden="true"
        >
            {text}
        </span>
    );
}

const ICONS = {
    profile: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
        </svg>
    ),
    admin: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
        </svg>
    ),
    logout: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M16 17l5-5-5-5" />
            <path d="M21 12H9" />
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        </svg>
    ),
};

export default function AvatarDropdown({ player, onProfile, onAdmin, onLogout }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (isMobile) return;
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, isMobile]);

    const close = () => setOpen(false);
    const fire = (cb) => { close(); cb && cb(); };

    const items = [
        onProfile && { key: 'profile', onClick: () => fire(onProfile), label: t('common.cabinet'), leading: ICONS.profile },
        player?.isAdmin && onAdmin && { key: 'admin', onClick: () => fire(onAdmin), label: t('common.admin'), leading: ICONS.admin },
        onLogout && { key: 'div', divider: true },
        onLogout && { key: 'logout', onClick: () => fire(onLogout), label: t('common.logout'), leading: ICONS.logout, danger: true },
    ].filter(Boolean);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`flex items-center gap-2 px-2 py-1 rounded-md border transition-all duration-200 ${
                    open
                        ? 'border-cyber-cyan/60 bg-cyber-cyan/10'
                        : 'border-cyber-border hover:border-cyber-cyan/40'
                }`}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Initials name={player?.name} />
                <div className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="text-[9px] font-display tracking-widest text-cyber-dim">{t('game.operator')}</span>
                    <span className="text-cyber-cyan glow-cyan font-display text-sm tracking-wider truncate max-w-[120px]">{player?.name}</span>
                </div>
                <span className={`text-[9px] text-cyber-dim transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {open && !isMobile && (
                <ul
                    role="menu"
                    className="absolute right-0 top-full mt-2 z-50 min-w-[180px] neon-panel py-1 shadow-neon-strong animate-popup-pop"
                >
                    {items.map((it) => {
                        if (it.divider) return <li key={it.key} className="h-px bg-cyber-border/40 my-1" />;
                        const palette = it.danger
                            ? 'text-cyber-pink hover:bg-cyber-pink/10'
                            : 'text-cyber-text hover:bg-cyber-border/30 hover:text-cyber-cyan';
                        return (
                            <li key={it.key}>
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={it.onClick}
                                    className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs font-display tracking-widest transition-colors ${palette}`}
                                >
                                    {it.leading}
                                    <span className="flex-1">{it.label}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {open && isMobile && createPortal(
                <BottomSheet
                    title={player?.name}
                    onClose={close}
                    items={items}
                />,
                document.body
            )}
        </div>
    );
}
